
/*
 * SHIPPING CALCULATOR — SKU-BASED VERSION
 * ---------------------------------------------------------
 * Purpose:
 * 1. Read the customer's Shopify basket.
 * 2. Identify panel sizes using product SKUs.
 * 3. Read actual variant weights from Shopify (grams).
 * 4. Determine pallet multiplier from the largest panel.
 * 5. Select MQP, QP or HP using existing weight limits.
 * 6. Find the delivery zone from the postcode.
 * 7. Look up the existing shipping rate.
 * 8. Display the quote on the Shopify page.
 *
 * Required assets, which must load BEFORE this file:
 * - shipping-postcode-zones.js
 * - shipping-rates.js
 *
 * IMPORTANT:
 * This calculates a delivery estimate. It does not create
 * a Shopify checkout shipping rate or submit an invoice.
 */

(function () {
  "use strict";


  /*
   * BLOCK 1 — SKU DIMENSION CONFIGURATION
   * -------------------------------------------------------
   * The dimension code is extracted from the SKU.
   *
   * 42 = 4x2 panel = multiplier 1
   * 82 = 8x2 panel = multiplier 2
   * 84 = 8x4 panel = multiplier 2
   * 48 = 4x8 panel = multiplier 2
   *
   * The code must appear as its own hyphen-separated
   * section of the SKU. This prevents the 416 or 418
   * product codes being mistaken for panel dimensions.
   */

  const SKU_DIMENSIONS = {
    "42": {
      panel: "4x2",
      multiplier: 1
    },
    "82": {
      panel: "8x2",
      multiplier: 2
    },
    "84": {
      panel: "8x4",
      multiplier: 2
    },
    "48": {
      panel: "4x8",
      multiplier: 2
    }
  };


  /*
   * BLOCK 2 — PALLET WEIGHT LIMITS
   * -------------------------------------------------------
   * These are retained from your original calculator.
   *
   * The first key is the pallet multiplier.
   * The second key is the pallet category.
   *
   * The calculator selects the smallest category that can
   * accommodate the total basket weight.
   */

  const PALLET_WEIGHT_LIMITS = {
    1: {
      MQP: 150,
      QP: 250,
      HP: 500
    },
    2: {
      MQP: 300,
      QP: 500,
      HP: 1000
    }
  };


  /*
   * BLOCK 3 — IDENTIFY THE PRODUCT FROM ITS SKU
   * -------------------------------------------------------
   * Shopify's cart.js provides the SKU for each cart line.
   *
   * We extract only a recognised dimension code that sits
   * between hyphens.
   *
   * Examples:
   * G416-FH-L-84-ST-P  -> 84
   * G416-HH-L-42-P     -> 42
   * CA-82-BL           -> 82
   * WFA-82-WA          -> 82
   *
   * Unknown or missing SKUs stop the calculation rather
   * than assigning an incorrect shipping rate.
   */

  function identifyPanel(item) {
    const sku = String(item.sku || "").trim().toUpperCase();

    if (!sku) {
      return null;
    }

    const matches = sku.match(/(?:^|-)(42|82|84|48)(?=-|$)/g);

    if (!matches || matches.length !== 1) {
      return null;
    }

    const dimensionCode = matches[0].replace(/^-/, "");

    const panel = SKU_DIMENSIONS[dimensionCode];

    if (!panel) {
      return null;
    }

    return {
      sku: sku,
      dimensionCode: dimensionCode,
      panel: panel.panel,
      multiplier: panel.multiplier
    };
  }


  /*
   * BLOCK 4 — ANALYSE THE SHOPIFY BASKET
   * -------------------------------------------------------
   * Each cart line contains:
   * - SKU
   * - Quantity
   * - Weight in grams (Shopify's item.grams)
   *
   * Convert grams to kilograms and multiply by quantity.
   *
   * The largest panel determines the pallet multiplier.
   * All products contribute to the total basket weight.
   */

  function analyseBasket(cart) {
    if (!cart.items || cart.items.length === 0) {
      throw new Error("Your basket is empty.");
    }

    let totalWeight = 0;
    let largestPanel = null;
    let multiplier = 1;

    const basketItems = [];

    cart.items.forEach(function (item) {
      const panel = identifyPanel(item);

      const productName =
        item.product_title ||
        item.title ||
        "Unknown product";

      if (!panel) {
        throw new Error(
          'Could not identify the panel size for "' +
          productName +
          '". Please check that its SKU contains a recognised ' +
          "dimension code (42, 82, 84 or 48)."
        );
      }

      const quantity = Number(item.quantity);

      if (!Number.isInteger(quantity) || quantity < 1) {
        throw new Error(
          'The quantity for "' + productName +
          '" is invalid.'
        );
      }

      /*
       * Shopify cart.js returns item.grams in grams.
       * Convert to kilograms for the pallet calculations.
       */
      const weightGrams = Number(item.grams);

      if (
        !Number.isFinite(weightGrams) ||
        weightGrams <= 0
      ) {
        throw new Error(
          'Shopify has no valid shipping weight for "' +
          productName +
          '". Please check the weight entered in the product variant.'
        );
      }

      const weightKg = weightGrams / 1000;

      // Add this product's total weight to the basket.
      totalWeight += weightKg * quantity;

      // The largest panel determines the multiplier.
      if (panel.multiplier > multiplier) {
        multiplier = panel.multiplier;
        largestPanel = panel.panel;
      }

      // Keep a record of each basket line for troubleshooting.
      basketItems.push({
        sku: panel.sku,
        panel: panel.panel,
        quantity: quantity,
        weightPerItem: weightKg,
        lineWeight: weightKg * quantity,
        multiplier: panel.multiplier
      });
    });

    return {
      totalWeight: totalWeight,
      largestPanel: largestPanel || "4x2",
      multiplier: multiplier,
      items: basketItems
    };
  }


  /*
   * BLOCK 5 — SELECT MQP, QP OR HP
   * -------------------------------------------------------
   * Use the multiplier established by the largest panel.
   * Compare total weight against the limits in ascending
   * order: MQP, QP and finally HP.
   *
   * If the weight exceeds the HP limit, return null.
   */

  function getPalletCategory(totalWeight, multiplier) {
    const limits = PALLET_WEIGHT_LIMITS[multiplier];

    if (!limits) {
      return null;
    }

    if (totalWeight <= limits.MQP) {
      return "MQP";
    }

    if (totalWeight <= limits.QP) {
      return "QP";
    }

    if (totalWeight <= limits.HP) {
      return "HP";
    }

    return null;
  }


  /*
   * BLOCK 6 — NORMALISE AND LOOK UP THE POSTCODE
   * -------------------------------------------------------
   * This is unchanged from your original calculator.
   *
   * Check all postcode-prefix rules and keep the LONGEST
   * matching prefix.
   */

  
  function findPostcodeZone(postcode) {
    // Remove spaces and convert to uppercase.
    const normalised = String(postcode || "")
      .toUpperCase()
      .replace(/\s+/g, "");

    // Validate the complete postcode.
    const validPostcode =
      /^(GIR0AA|[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2})$/;

    if (!validPostcode.test(normalised)) {
      throw new Error(
        "Please enter a complete, valid UK postcode."
      );
    }

    // Remove the final three characters to get the
    // postcode area/district used for shipping zones.
    const postcodePrefix = normalised.slice(0, -3);

    const rules = window.SHIPPING_POSTCODE_ZONES;

    if (!Array.isArray(rules)) {
      throw new Error(
        "The postcode-zone data has not loaded correctly."
      );
    }

    let matchedZone = null;
    let longestPrefixLength = 0;

    // Match against the shortened postcode.
    rules.forEach(function (rule) {
      const prefix = String(rule.prefix || "")
        .toUpperCase()
        .replace(/\s+/g, "");

      if (
        prefix &&
        postcodePrefix.startsWith(prefix) &&
        prefix.length > longestPrefixLength
      ) {
        longestPrefixLength = prefix.length;
        matchedZone = Number(rule.zone);
      }
    });

    if (
      matchedZone === null ||
      !Number.isFinite(matchedZone)
    ) {
      throw new Error(
        "We couldn't find a shipping zone for this postcode. " +
        "Please contact us for a delivery quote."
      );
    }

    return matchedZone;
  }


  /*
   * BLOCK 7 — LOOK UP THE SHIPPING PRICE
   * -------------------------------------------------------
   * This is unchanged from your original calculator.
   *
   * Rate structure:
   * Zone -> Pallet category -> Multiplier
   */

  function getShippingPrice(zone, palletSize, multiplier) {
    const rates = window.SHIPPING_RATES;

    if (!rates || typeof rates !== "object") {
      throw new Error(
        "The shipping-rate data has not loaded correctly."
      );
    }

    const price = rates[zone]?.[palletSize]?.[multiplier];

    if (
      typeof price !== "number" ||
      !Number.isFinite(price) ||
      price < 0
    ) {
      return null;
    }

    return price;
  }


  /*
   * BLOCK 8 — FORMAT PRICES
   * -------------------------------------------------------
   * Shipping rates are stored in pounds.
   * Shopify's cart total_price is stored in pence.
   */

  function formatGBP(amount) {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP"
    }).format(amount);
  }


  /*
   * INVOICE INTEGRATION — CART FINGERPRINT
   * -------------------------------------------------------
   * Retained from your original calculator.
   *
   * The invoice handler uses this to check that the quote
   * still matches the customer's current basket.
   */

  function getCartSignature(cart) {
    return JSON.stringify({
      currency: cart.currency,
      total_price: cart.total_price,
      items: (cart.items || []).map(function (item) {
        return {
          key: item.key,
          quantity: item.quantity,
          variant_id: item.variant_id,
          final_line_price: item.final_line_price
        };
      })
    });
  }


  /*
   * BLOCK 9 — FIND THE SHOPIFY PAGE ELEMENTS
   * -------------------------------------------------------
   * These selectors are retained from your original file.
   */

  function initialiseCalculator() {
    const postcodeInput =
      document.querySelector("[data-shipping-postcode]") ||
      document.querySelector(
        'input[autocomplete="postal-code"], ' +
        'input[name*="postcode" i], ' +
        'input[id*="postcode" i]'
      );

    const calculateButton =
      document.querySelector("[data-calculate-shipping]") ||
      Array.from(
        document.querySelectorAll("button, input[type='button']")
      ).find(function (button) {
        const label = (
          button.textContent || button.value || ""
        ).toLowerCase();

        return (
          label.includes("calculate shipping") ||
          label.includes("generate shipping cost")
        );
      });

    const deliveryMessage =
      document.querySelector("[data-delivery-message]");

    const deliveryPrice =
      document.querySelector("[data-delivery-price]");

    const basketTotal =
      document.querySelector("[data-basket-total]");

    const invoiceButton =
      document.querySelector("[data-request-invoice]");

    const invoiceMessage =
      document.querySelector("[data-invoice-message]");

    const calculatorMessage =
      document.querySelector("[data-shipping-calculator-message]");

    if (!postcodeInput || !calculateButton) {
      console.warn(
        "Shipping calculator: postcode input or calculation " +
        "button not found. Check the required data attributes."
      );
      return;
    }

    if (
      calculateButton.dataset.shippingCalculatorBound === "true"
    ) {
      return;
    }

    calculateButton.dataset.shippingCalculatorBound = "true";

    let quoteIsValid = false;


    /*
     * BLOCK 10 — UPDATE MESSAGES AND RESET OLD QUOTES
     * -------------------------------------------------------
     * Retained from your original calculator.
     */

    function showMessage(message) {
      if (calculatorMessage) {
        calculatorMessage.textContent = message;
      }
    }

    function invalidateQuote() {
      if (!quoteIsValid) {
        return;
      }

      quoteIsValid = false;

      if (deliveryPrice) {
        deliveryPrice.textContent = "—";
      }

      if (deliveryMessage) {
        deliveryMessage.textContent =
          "Your postcode changed. Recalculate delivery.";
      }

      if (basketTotal) {
        basketTotal.textContent = "—";
      }

      if (invoiceButton) {
        invoiceButton.disabled = true;
      }

      if (invoiceMessage) {
        invoiceMessage.textContent =
          "Calculate your delivery cost before requesting an invoice.";
      }

      showMessage(
        "Please recalculate your delivery quote."
      );
    }

    postcodeInput.addEventListener("input", invalidateQuote);


    /*
     * BLOCK 11 — RUN THE SHIPPING CALCULATION
     * -------------------------------------------------------
     * 1. Fetch the current Shopify basket.
     * 2. Identify products using SKUs.
     * 3. Calculate weight using Shopify variant weights.
     * 4. Determine the pallet category and multiplier.
     * 5. Find the postcode zone.
     * 6. Look up the existing rate.
     * 7. Display the quote and basket total.
     */

    calculateButton.addEventListener("click", async function (event) {
      event.preventDefault();

      quoteIsValid = false;

      // Do not reuse a previous quote during recalculation.
      window.SHIPPING_CURRENT_QUOTE = null;

      if (invoiceButton) {
        invoiceButton.disabled = true;
      }

      if (deliveryPrice) {
        deliveryPrice.textContent = "—";
      }

      if (basketTotal) {
        basketTotal.textContent = "—";
      }

      calculateButton.disabled = true;

      showMessage("Calculating your delivery quote…");

      try {
        /*
         * Fetch the latest Shopify basket.
         */
        const rootPath = window.Shopify?.routes?.root || "/";

        const response = await fetch(rootPath + "cart.js", {
          headers: {
            Accept: "application/json"
          }
        });

        if (!response.ok) {
          throw new Error(
            "We couldn't retrieve your basket. Please try again."
          );
        }

        const cart = await response.json();

        document.dispatchEvent(
          new CustomEvent("shipping:cart-updated", {
            detail: {
              cart: cart
            }
          })
        );

        /*
         * The existing rate table is in GBP.
         */
        if (cart.currency && cart.currency !== "GBP") {
          throw new Error(
            "This shipping calculator currently supports GBP orders only."
          );
        }

        /*
         * Analyse the basket using SKUs and actual Shopify weights.
         */
        const basket = analyseBasket(cart);

        /*
         * Select the smallest pallet category that supports
         * the combined basket weight.
         */
        const palletSize = getPalletCategory(
          basket.totalWeight,
          basket.multiplier
        );

        if (!palletSize) {
          throw new Error(
            "Your basket exceeds the maximum supported pallet weight " +
            "of " +
            PALLET_WEIGHT_LIMITS[basket.multiplier].HP +
            " kg. Please contact us for a delivery quote."
          );
        }

        /*
         * Find the postcode's zone using the longest matching prefix.
         */
        const zone = findPostcodeZone(postcodeInput.value);

        /*
         * Retrieve the existing rate for this zone,
         * pallet category and multiplier.
         */
        const shippingPrice = getShippingPrice(
          zone,
          palletSize,
          basket.multiplier
        );

        if (shippingPrice === null) {
          throw new Error(
            "No shipping rate was found for Zone " +
            zone +
            ", " +
            palletSize +
            ", multiplier " +
            basket.multiplier +
            ". Please contact us for a quote."
          );
        }

        /*
         * Calculate the displayed total.
         * Shopify total_price is in pence.
         * Shipping rates are in pounds.
         */
        const shippingInPence = Math.round(shippingPrice * 100);

        const totalInPence =
          Number(cart.total_price) + shippingInPence;

        if (!Number.isFinite(totalInPence)) {
          throw new Error(
            "We couldn't calculate the basket total."
          );
        }

        /*
         * Update the basket summary with the successful quote.
         */
        if (deliveryPrice) {
          deliveryPrice.textContent = formatGBP(shippingPrice);
        }

        if (deliveryMessage) {
          deliveryMessage.textContent =
            "Delivery estimate — Zone " +
            zone +
            ", " +
            palletSize +
            ", " +
            basket.multiplier +
            "× pallet";
        }

        if (basketTotal) {
          basketTotal.textContent =
            formatGBP(totalInPence / 100);
        }

        /*
         * Publish the successful quote for the existing
         * invoice-request handler.
         */
        window.SHIPPING_CURRENT_QUOTE = {
          shippingPrice: shippingPrice,
          zone: zone,
          palletSize: palletSize,
          multiplier: basket.multiplier,
          totalWeight: basket.totalWeight,
          totalInPence: totalInPence,
          postcode: postcodeInput.value.trim().toUpperCase(),
          currency: cart.currency || "GBP",
          cartSignature: getCartSignature(cart)
        };

        quoteIsValid = true;

        if (invoiceButton) {
          invoiceButton.disabled = false;
        }

        if (invoiceMessage) {
          invoiceMessage.textContent =
            "Delivery estimate calculated. You can now request an invoice.";
        }

        showMessage(
          "Estimated delivery: " +
          formatGBP(shippingPrice) +
          " · Total including delivery: " +
          formatGBP(totalInPence / 100) +
          " · Combined panel weight: " +
          basket.totalWeight.toFixed(2) +
          " kg"
        );

      } catch (error) {
        /*
         * Show useful errors to the customer and log details
         * for troubleshooting.
         */
        console.error(
          "Shipping calculator error:",
          error
        );

        if (deliveryMessage) {
          deliveryMessage.textContent = error.message;
        }

        if (deliveryPrice) {
          deliveryPrice.textContent = "—";
        }

        if (basketTotal) {
          basketTotal.textContent = "—";
        }

        if (invoiceButton) {
          invoiceButton.disabled = true;
        }

        if (invoiceMessage) {
          invoiceMessage.textContent =
            "A valid delivery quote is required before requesting an invoice.";
        }

        showMessage(error.message);

      } finally {
        calculateButton.disabled = false;
      }
    });
  }


  /*
   * BLOCK 12 — START THE CALCULATOR
   * -------------------------------------------------------
   * Retained from your original calculator.
   *
   * Supports normal page loading and Shopify theme-editor
   * section reloads.
   */

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initialiseCalculator
    );
  } else {
    initialiseCalculator();
  }

  document.addEventListener(
    "shopify:section:load",
    initialiseCalculator
  );

})();
